// src/utils/notify.js
//
// THE one place toasts come from (audit 4.1 / 7.5).
//
//   import { Notify } from "<rel>/utils/notify";   // legacy-compatible API
//   import { notify } from "<rel>/utils/notify";   // notify.success/error/warning/info
//
// - One Notiflix import path (the earlier two-bundle problem is gone, so a new
//   import can no longer silently lose the theme).
// - Standard timings: success 3s, info 4s, warning 5s, error 7s with a close button.
// - Message copy rules: sentence case, no trailing period, no raw technical text.
// - Colours are read from the --eex-* tokens at init time so the toast matches the rest of the UI.
import { Notify as NotiflixNotify } from "notiflix/build/notiflix-notify-aio";

const TIMEOUT = { success: 3000, info: 4000, warning: 5000, failure: 7000 };

const cssVar = (name, fallback) => {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch (e) {
    return fallback;
  }
};

// Raw library / network strings that should never reach a user.
const TECHNICAL = [
  /^request failed with status code/i,
  /^network error$/i,
  /^timeout of \d+ms exceeded/i,
  /^(type|reference|syntax|range)error/i,
  /^error:/i,
  /execution reverted/i,
  /call_exception|call revert exception|unpredictable_gas_limit/i,
  /^\[object object\]$/i,
  /^undefined$|^null$/i,
];
const FRIENDLY_FALLBACK = "Something went wrong. Please try again";

const cleanMessage = (input) => {
  let msg = input == null ? "" : typeof input === "string" ? input : (input && input.message) || String(input);
  msg = msg.replace(/\s+/g, " ").trim();
  if (!msg || TECHNICAL.some((re) => re.test(msg))) return FRIENDLY_FALLBACK;
  if (/^[a-z]/.test(msg)) msg = msg.charAt(0).toUpperCase() + msg.slice(1);
  // no trailing period (but keep ellipses)
  if (/[^.]\.$/.test(msg)) msg = msg.slice(0, -1);
  return msg;
};

let initialised = false;
export const initNotify = () => {
  if (initialised) return;
  initialised = true;
  const ink = cssVar("--eex-navy", "#0f2942");
  const surface = cssVar("--eex-surface-solid", "#ffffff");
  const base = (cls, icon, color) => ({
    background: surface,
    textColor: ink,
    childClassName: `ee-notify ee-notify--${cls}`,
    fontAwesomeClassName: icon,
    fontAwesomeIconColor: color,
    notiflixIconColor: color,
  });
  const success = cssVar("--eex-success", "#10b981");
  const danger = cssVar("--eex-danger", "#ef4444");
  const warn = cssVar("--eex-warning", "#f59e0b");
  const primary = cssVar("--eex-primary", "#2563eb");
  NotiflixNotify.init({
    width: "360px",
    position: "right-top",
    distance: "20px",
    opacity: 1,
    borderRadius: "10px",
    timeout: TIMEOUT.success,
    messageMaxLength: 220,
    backOverlay: false,
    fontFamily: cssVar("--eex-font-body", "IBM Plex Sans, sans-serif").replace(/"/g, ""),
    fontSize: "14px",
    cssAnimation: true,
    cssAnimationDuration: 260,
    cssAnimationStyle: "from-right",
    closeButton: false,
    useIcon: true,
    useFontAwesome: true,
    fontAwesomeIconStyle: "basic",
    fontAwesomeIconSize: "20px",
    plainText: true,
    showOnlyTheLastOne: false,
    clickToClose: true,
    zindex: 2000, // --eex-z-toast
    success: base("success", "fas fa-check-circle", success),
    failure: base("failure", "fas fa-times-circle", danger),
    warning: base("warning", "fas fa-exclamation-triangle", warn),
    info: base("info", "fas fa-info-circle", primary),
  });
};

// Notiflix lets callers pass (msg), (msg, cb), (msg, opts) or (msg, cb, opts).
const send = (type, message, a, b) => {
  initNotify();
  let cb;
  let opts = {};
  if (typeof a === "function") {
    cb = a;
    opts = b || {};
  } else if (a && typeof a === "object") {
    opts = a;
  }
  const merged = { timeout: TIMEOUT[type], ...opts };
  if (type === "failure") {
    // errors stay long enough to read, and can be dismissed
    merged.closeButton = opts.closeButton !== false;
    merged.clickToClose = true;
  }
  return cb
    ? NotiflixNotify[type](cleanMessage(message), cb, merged)
    : NotiflixNotify[type](cleanMessage(message), merged);
};

// Legacy-compatible surface: Notify.success / failure / warning / info
export const Notify = {
  init: initNotify,
  success: (m, a, b) => send("success", m, a, b),
  failure: (m, a, b) => send("failure", m, a, b),
  warning: (m, a, b) => send("warning", m, a, b),
  info: (m, a, b) => send("info", m, a, b),
  merge: (o) => NotiflixNotify.merge(o),
};

// Preferred surface for new code
export const notify = {
  success: Notify.success,
  error: Notify.failure,
  warning: Notify.warning,
  info: Notify.info,
};

export default notify;
